import type { ValueOf } from './util/typeUtils.js';

// Fixed v5 UUID namespaces used to deterministically derive Chat-domain aggregate ids.
// Hardcoded the same way other namespace/aggregate-id constants are in this repo
// (see apps/cpool-backend/res/initial/**/constants/constants.ts) — never generate these at runtime.
export const CHAT_AGGREGATE_NAMESPACE = '8e62ba39-4c45-4396-bb49-078cbfe07889';
export const CHAT_READ_CURSOR_NAMESPACE = 'f92ef34a-6c64-41c0-a23d-d9a2fd8bc697';

// Meta's WhatsApp Business "customer service window": free-form messages may only be sent within
// 24h of the customer's most recent inbound message (see cpool-backend's `sendMessage` command).
// Template messages are exempt. Shared with the frontend so the composer/status-pill UI (KC-6378)
// derives the exact same open/expired boundary the backend enforces, not an independently
// reimplemented copy of "24 hours".
export const WHATSAPP_CUSTOMER_SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;

export const CHAT_MESSAGE_DIRECTIONS = {
    INBOUND: 'inbound',
    OUTBOUND: 'outbound',
} as const;

export type ChatMessageDirection = ValueOf<typeof CHAT_MESSAGE_DIRECTIONS>;

export const CHAT_MESSAGE_TYPES = {
    MEDIA: 'media',
    TEMPLATE: 'template',
    TEXT: 'text',
} as const;

export type ChatMessageType = ValueOf<typeof CHAT_MESSAGE_TYPES>;

export const CHAT_MESSAGE_FAILED_REASONS = {
    MEDIA_FETCH_FAILED: 'MEDIA_FETCH_FAILED',
    META_API_ERROR: 'META_API_ERROR',
    RATE_LIMITED: 'RATE_LIMITED',
    RECIPIENT_OUTSIDE_24H_WINDOW: 'RECIPIENT_OUTSIDE_24H_WINDOW',
    SENDER_NUMBER_UNAVAILABLE: 'SENDER_NUMBER_UNAVAILABLE',
} as const;

export type CHAT_MESSAGE_FAILED_REASON = ValueOf<typeof CHAT_MESSAGE_FAILED_REASONS>;

export const CHAT_MESSAGE_STATUSES = {
    DELIVERED: 'delivered',
    FAILED: 'failed',
    QUEUED: 'queued',
    READ: 'read',
    RECEIVED: 'received',
    SENT: 'sent',
} as const;

export type ChatMessageStatus = ValueOf<typeof CHAT_MESSAGE_STATUSES>;

export const CHAT_MEDIA_DELETION_FAILED_REASONS = {
    MEDIA_NOT_FOUND: 'MEDIA_NOT_FOUND',
    META_API_ERROR: 'META_API_ERROR',
} as const;

export type CHAT_MEDIA_DELETION_FAILED_REASON = ValueOf<typeof CHAT_MEDIA_DELETION_FAILED_REASONS>;

export const CHAT_INBOUND_READ_RECEIPT_FAILED_REASONS = {
    MESSAGE_NOT_FOUND: 'MESSAGE_NOT_FOUND',
    META_API_ERROR: 'META_API_ERROR',
    RATE_LIMITED: 'RATE_LIMITED',
} as const;

export type CHAT_INBOUND_READ_RECEIPT_FAILED_REASON = ValueOf<typeof CHAT_INBOUND_READ_RECEIPT_FAILED_REASONS>;

export const CHAT_COMMAND_ERRORS = {
    ADVISER_HAS_NO_GENDER: 'ADVISER_HAS_NO_GENDER',
    ADVISER_HAS_NO_PHONE: 'ADVISER_HAS_NO_PHONE',
    ADVISER_HAS_NO_TITLE: 'ADVISER_HAS_NO_TITLE',
    APPLICANT_HAS_NO_DEMANDED_DOCUMENTS: 'APPLICANT_HAS_NO_DEMANDED_DOCUMENTS',
    APPLICANT_HAS_NO_SALUTATION: 'APPLICANT_HAS_NO_SALUTATION',
    CALLING_USER_HAS_NO_DIVISION: 'CALLING_USER_HAS_NO_DIVISION',
    CALLING_USER_HAS_NO_PID: 'CALLING_USER_HAS_NO_PID',
    CHAT_ALREADY_RESOLVED: 'CHAT_ALREADY_RESOLVED',
    CHAT_ID_MISMATCH: 'CHAT_ID_MISMATCH',
    CHAT_NOT_RESOLVED: 'CHAT_NOT_RESOLVED',
    CHAT_PHONE_NUMBER_MISMATCH: 'CHAT_PHONE_NUMBER_MISMATCH',
    CO_APPLICANT_DATA_PROTECTION_NOT_AGREED: 'CO_APPLICANT_DATA_PROTECTION_NOT_AGREED',
    CUSTOMER_IDENTITY_NOT_ON_LOAN_REQUEST: 'CUSTOMER_IDENTITY_NOT_ON_LOAN_REQUEST',
    DIVISION_HAS_NO_WHATSAPP_NUMBER: 'DIVISION_HAS_NO_WHATSAPP_NUMBER',
    INVALID_CUSTOMER_PHONE: 'INVALID_CUSTOMER_PHONE',
    LOAN_REQUEST_NOT_ASSIGNED: 'LOAN_REQUEST_NOT_ASSIGNED',
    LOAN_REQUEST_NOT_LINKED_TO_CHAT: 'LOAN_REQUEST_NOT_LINKED_TO_CHAT',
    LOAN_REQUEST_REQUIRED_FOR_TEMPLATE: 'LOAN_REQUEST_REQUIRED_FOR_TEMPLATE',
    OUTSIDE_24H_WINDOW: 'OUTSIDE_24H_WINDOW',
    PARTNER_HAS_NO_EMAIL: 'PARTNER_HAS_NO_EMAIL',
    PARTNER_HAS_NO_NAME: 'PARTNER_HAS_NO_NAME',
    PARTNER_ID_REQUIRED: 'PARTNER_ID_REQUIRED',
    PARTNER_NOT_ASSIGNED: 'PARTNER_NOT_ASSIGNED',
    PARTNER_REQUIRED_FOR_TEMPLATE: 'PARTNER_REQUIRED_FOR_TEMPLATE',
    TEMPLATE_NOT_IN_CATALOG: 'TEMPLATE_NOT_IN_CATALOG',
    TEMPLATE_NOT_SENDABLE: 'TEMPLATE_NOT_SENDABLE',
    TEMPLATE_VALUE_HAS_INVALID_CHARS: 'TEMPLATE_VALUE_HAS_INVALID_CHARS',
    TEMPLATE_VALUE_TOO_LONG: 'TEMPLATE_VALUE_TOO_LONG',
} as const;

export type ChatCommandError = ValueOf<typeof CHAT_COMMAND_ERRORS>;

export const CHAT_INBOUND_READ_RECEIPT_MAX_SCHEDULED_RETRIES = 3;

export const CHAT_INBOUND_READ_RECEIPT_RETRY_DELAYS_MS = [60_000, 300_000, 900_000] as const;

// Delay before retrying a failed Chat_Linked publish (see ensureLinked). Same magnitude as the
// first inbound-read-receipt retry — short enough that a single dropped inbound message doesn't
// leave a chat unlinked for long, without hammering the aggregate immediately.
export const CHAT_LINK_RETRY_DELAY_MS = 60_000;
